import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Input, Button, Card, Space, Switch, message, Select } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { strategyApi } from '@tradermind/shared/api';
import type { CreateStrategyRequest, UpdateStrategyRequest } from '@tradermind/shared/types';

const { TextArea } = Input;

export default function StrategyFormPage() {
  const { t } = useTranslation(['strategy', 'common']);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const isEdit = !!id;

  const { data: strategyData, isLoading } = useQuery({
    queryKey: ['strategy', id],
    queryFn: () => strategyApi.getStrategyById(Number(id)),
    enabled: isEdit,
  });

  const createMutation = useMutation({
    mutationFn: strategyApi.createStrategy,
    onSuccess: () => {
      message.success(t('strategy:messages.created'));
      queryClient.invalidateQueries({ queryKey: ['strategies'] });
      navigate('/strategies');
    },
    onError: () => {
      message.error(t('common:error.save_failed'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateStrategyRequest }) =>
      strategyApi.updateStrategy(id, data),
    onSuccess: () => {
      message.success(t('strategy:messages.updated'));
      queryClient.invalidateQueries({ queryKey: ['strategies'] });
      navigate('/strategies');
    },
    onError: () => {
      message.error(t('common:error.save_failed'));
    },
  });

  useEffect(() => {
    if (strategyData?.data) {
      const strategy = strategyData.data;
      form.setFieldsValue({
        name: strategy.name,
        timeframe: strategy.timeframe,
        description: strategy.description,
        conditions: strategy.conditions || [{ id: Date.now().toString(), description: '' }],
        is_active: strategy.is_active,
      });
    }
  }, [strategyData, form]);

  const onFinish = (values: any) => {
    const conditions = values.conditions
      .filter((c: any) => c.description.trim())
      .map((c: any) => ({ id: c.id || Date.now().toString(), description: c.description }));

    if (isEdit) {
      const data: UpdateStrategyRequest = {
        name: values.name,
        timeframe: values.timeframe,
        description: values.description,
        conditions,
        is_active: values.is_active ?? true,
      };
      updateMutation.mutate({ id: Number(id), data });
    } else {
      const data: CreateStrategyRequest = {
        name: values.name,
        timeframe: values.timeframe,
        description: values.description,
        conditions,
      };
      createMutation.mutate(data);
    }
  };

  const timeframeOptions = [
    { label: t('strategy:timeframes.M1'), value: 'M1' },
    { label: t('strategy:timeframes.M5'), value: 'M5' },
    { label: t('strategy:timeframes.M15'), value: 'M15' },
    { label: t('strategy:timeframes.M30'), value: 'M30' },
    { label: t('strategy:timeframes.H1'), value: 'H1' },
    { label: t('strategy:timeframes.H4'), value: 'H4' },
    { label: t('strategy:timeframes.D1'), value: 'D1' },
    { label: t('strategy:timeframes.W1'), value: 'W1' },
    { label: t('strategy:timeframes.MN'), value: 'MN' },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1>{isEdit ? t('strategy:edit_strategy') : t('strategy:add_strategy')}</h1>
      </div>

      <Card loading={isLoading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            conditions: [{ id: Date.now().toString(), description: '' }],
            is_active: true,
          }}
        >
          <Form.Item
            label={t('strategy:strategy_name')}
            name="name"
            rules={[{ required: true, message: t('strategy:messages.name_required') }]}
          >
            <Input placeholder={t('strategy:strategy_name_placeholder')} />
          </Form.Item>

          <Form.Item label={t('strategy:timeframe')} name="timeframe">
            <Select
              placeholder={t('strategy:timeframe_placeholder')}
              options={timeframeOptions}
              allowClear
            />
          </Form.Item>

          <Form.Item label={t('strategy:description')} name="description">
            <TextArea
              rows={4}
              placeholder={t('strategy:description_placeholder')}
            />
          </Form.Item>

          <Form.Item label={t('strategy:conditions')} required>
            <div style={{ marginBottom: 8, color: '#666', fontSize: 14 }}>
              {t('strategy:conditions_hint')}
            </div>
            <Form.List
              name="conditions"
              rules={[
                {
                  validator: async (_, conditions) => {
                    if (!conditions || conditions.filter((c: any) => c.description.trim()).length === 0) {
                      return Promise.reject(new Error(t('strategy:messages.conditions_required')));
                    }
                  },
                },
              ]}
            >
              {(fields, { add, remove }, { errors }) => (
                <>
                  {fields.map((field, index) => (
                    <Space key={field.key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                      <Form.Item
                        {...field}
                        name={[field.name, 'description']}
                        rules={[{ required: true, message: t('common:required') }]}
                        style={{ flex: 1, marginBottom: 0 }}
                      >
                        <Input
                          placeholder={t('strategy:condition_placeholder')}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                      <Form.Item
                        {...field}
                        name={[field.name, 'id']}
                        hidden
                        initialValue={Date.now().toString() + index}
                      >
                        <Input />
                      </Form.Item>
                      {fields.length > 1 && (
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(field.name)}
                        />
                      )}
                    </Space>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add({ id: Date.now().toString(), description: '' })}
                      block
                      icon={<PlusOutlined />}
                    >
                      {t('strategy:add_condition')}
                    </Button>
                  </Form.Item>
                  <Form.ErrorList errors={errors} />
                </>
              )}
            </Form.List>
          </Form.Item>

          <Form.Item label={t('common:status')} name="is_active" valuePropName="checked">
            <Switch checkedChildren={t('common:active')} unCheckedChildren={t('common:inactive')} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createMutation.isPending || updateMutation.isPending}
              >
                {t('strategy:save_strategy')}
              </Button>
              <Button onClick={() => navigate('/strategies')}>
                {t('common:cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
